import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const FORM_ID = 'daimkjboh653ivfvogi0';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/daimkjboh653ivfvogi0';
const MAX_LEN = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

const steps = [
  { icon: 'ri-inbox-archive-line', title: '收到线索', desc: '编辑在 24 小时内完成初步分拣，判断是否进入核实流程。' },
  { icon: 'ri-git-branch-line', title: '交叉核实', desc: '至少寻找两个独立信源比对，并尽量用公开材料印证关键节点。' },
  { icon: 'ri-file-list-3-line', title: '整理发布', desc: '以时间线形式呈现，标注核实状态，非公开当事人做匿名处理。' },
];

export default function ContactPage() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [len, setLen] = useState(0);

  useSeo({
    title: '我要爆料与联系我们 - 17爆料网',
    description:
      '向17爆料网投稿你的第一手线索或内部消息。支持匿名提交，编辑会交叉核实后整理发布，并对线索来源保密。',
    keywords: '17爆料网,17爆料网投稿,17爆料网爆料入口,17爆料网联系',
    path: '/contact',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '我要爆料与联系我们 - 17爆料网',
        url: 'https://dyfscl.cn/contact',
        description: '向17爆料网投稿线索或联系编辑。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteConfig.name,
        url: 'https://dyfscl.cn/',
        email: siteConfig.email,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: '投稿与线索',
          email: siteConfig.email,
          availableLanguage: ['zh-CN'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dyfscl.cn/' },
          { '@type': 'ListItem', position: 2, name: '我要爆料', item: 'https://dyfscl.cn/contact' },
        ],
      },
    ],
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, String(value)));

      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setLen(0);
        form.reset();
      } else {
        setStatus('error');
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查网络后重试。');
    }
  };

  return (
    <>
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 pt-6 pb-12">
          <Breadcrumbs items={[{ label: '我要爆料' }]} />
          <div className="mt-8 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-widest text-accent-700 font-label mb-4">
              <span className="w-6 h-px bg-accent-500"></span>
              SUBMIT A TIP
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">
              把你手里的线索，交给会核实的人
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              支持匿名提交。请尽量写清事件发生的时间、地点、你所处的角色，以及能佐证的关键细节。
              我们会交叉核实后整理发布，并对线索来源保密。
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm text-foreground-700 mb-2">
                    你的称呼 <span className="text-accent-600">*</span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="可用化名"
                    className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm text-foreground-700 mb-2">
                    联系邮箱 <span className="text-accent-600">*</span>
                  </span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
                  />
                </label>
              </div>

              <label className="block mt-4">
                <span className="block text-sm text-foreground-700 mb-2">线索类型</span>
                <select
                  name="topic"
                  defaultValue="娱乐现场"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm outline-none cursor-pointer focus:border-primary-400 transition-colors"
                >
                  <option>娱乐现场</option>
                  <option>科技内幕</option>
                  <option>财经风向</option>
                  <option>民生观察</option>
                  <option>生活方式</option>
                  <option>其他方向</option>
                </select>
              </label>

              <label className="block mt-4">
                <span className="block text-sm text-foreground-700 mb-2">
                  线索描述 <span className="text-accent-600">*</span>
                </span>
                <textarea
                  name="message"
                  required
                  maxLength={MAX_LEN}
                  rows={7}
                  onChange={(e) => setLen(e.target.value.length)}
                  placeholder="例如：事件发生的大致时间、地点、你如何得知这条信息、有哪些可以佐证的细节……"
                  className="w-full px-3 py-3 rounded-md border border-background-300 bg-background-50 text-sm leading-relaxed outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400 resize-none"
                ></textarea>
                <span className="mt-1.5 block text-right text-xs text-foreground-400">
                  {len} / {MAX_LEN}
                </span>
              </label>

              <input
                type="text"
                name="website_alt"
                className="form-extra-slot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-accent-600 text-background-50 text-sm font-medium hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  <i className="ri-send-plane-fill"></i>
                  {status === 'submitting' ? '提交中…' : '提交线索'}
                </button>
                <p className="text-xs text-foreground-500">提交后我们会尽快联系，请留意邮箱。</p>
              </div>

              {status === 'success' && (
                <p className="mt-4 text-sm text-secondary-700 bg-secondary-100 rounded-md px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill"></i>
                  提交成功，感谢你的信任，编辑会尽快跟进。
                </p>
              )}
              {status === 'error' && (
                <p className="mt-4 text-sm text-accent-700 bg-accent-100 rounded-md px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-fill"></i>
                  {formError}
                </p>
              )}
            </form>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
              <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-700"></i>
                直接联系
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                不方便填写表单？直接写信到投稿邮箱，同样会被认真对待。
              </p>
              <p className="mt-3 font-heading font-bold text-foreground-950">{siteConfig.email}</p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading font-bold text-base text-foreground-950 mb-4">提交之后会发生什么</h2>
              <ol className="space-y-4">
                {steps.map((s, i) => (
                  <li key={s.title} className="flex gap-3">
                    <span className="shrink-0 w-9 h-9 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-700">
                      <i className={s.icon}></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">
                        {i + 1}. {s.title}
                      </span>
                      <span className="block mt-1 text-xs text-foreground-500 leading-relaxed">{s.desc}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-sm text-foreground-600 leading-relaxed">
              <p className="flex items-start gap-2">
                <i className="ri-lock-line text-secondary-600 mt-0.5"></i>
                我们不会公开你的身份信息，也不会把联系方式用于内容以外的用途。
              </p>
              <p className="mt-3 flex items-start gap-2">
                <i className="ri-scales-3-line text-secondary-600 mt-0.5"></i>
                涉及与公共利益无关的私人细节，不在我们的整理范围之内。
              </p>
              <p className="mt-4">
                想先了解我们的处理方式？看看
                <Link to="/faq" className="mx-1 text-primary-700 hover:text-primary-800 cursor-pointer">
                  常见问题
                </Link>
                或
                <Link to="/about" className="mx-1 text-primary-700 hover:text-primary-800 cursor-pointer">
                  关于我们
                </Link>
                。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}